import React from 'react';
import { ClientDTO } from '../../services/clientApi';
import { getDisplayTags } from '../../utils/clientDisplay';
type AddressValidationStatus = 'checking' | 'valid' | 'warning' | 'error';

interface ClientAddressValidationState {
  status: AddressValidationStatus;
  confidence?: number;
  warning?: string;
}

interface ClientListProps {
  clients: ClientDTO[];
  onView: (client: ClientDTO) => void;
  isLoading?: boolean;
  addressValidationStatusByClientId?: Record<string, ClientAddressValidationState>;
}

export const ClientList: React.FC<ClientListProps> = ({
  clients,
  onView,
  isLoading,
  addressValidationStatusByClientId,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun client</h3>
        <p className="mt-1 text-sm text-gray-500">Commencez par créer un nouveau client.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Client
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Contact
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Localisation
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Tags
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Statut
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {clients.map((client) => {
            const displayTags = getDisplayTags(client);

            return (
              <tr
                key={client.id}
                className="cursor-pointer hover:bg-gray-50 focus:bg-blue-50 focus:outline-none"
                onClick={() => onView(client)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onView(client);
                  }
                }}
                tabIndex={0}
                aria-label={`Voir la fiche de ${client.firstName} ${client.lastName}`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div
                        title="Voir les informations du client"
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 transition"
                      >
                        <span className="text-blue-600 font-medium text-sm">
                          {client.firstName[0]}{client.lastName[0]}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {client.firstName} {client.lastName}
                      </div>
                      {client.company && (
                        <div className="text-sm text-gray-500">{client.company}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{client.email}</div>
                  <div className="text-sm text-gray-500">{client.phone}</div>
                </td>
                <td className="px-6 py-4">
                  {client.id && addressValidationStatusByClientId?.[client.id] && (
                    <div className="mb-1 flex items-center gap-1.5">
                      {addressValidationStatusByClientId[client.id].status === 'valid' && (
                        <span
                          className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700"
                          title={`Adresse vérifiée (${Math.round((addressValidationStatusByClientId[client.id].confidence || 0) * 100)}%)`}
                        >
                          Adresse vérifiée
                        </span>
                      )}

                      {addressValidationStatusByClientId[client.id].status === 'warning' && (
                        <span
                          className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                          title={addressValidationStatusByClientId[client.id].warning || 'Adresse à vérifier'}
                        >
                          Adresse à vérifier
                        </span>
                      )}

                      {addressValidationStatusByClientId[client.id].status === 'checking' && (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                          Vérification...
                        </span>
                      )}

                      {addressValidationStatusByClientId[client.id].status === 'error' && (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                          Vérification indisponible
                        </span>
                      )}
                    </div>
                  )}

                  <div className="text-sm text-gray-900">{client.address}</div>
                  <div className="text-sm text-gray-500">
                    {client.postalCode} {client.city}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {displayTags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {tag}
                      </span>
                    ))}
                    {displayTags.length > 3 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        +{displayTags.length - 3}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      client.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {client.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
